(function() {
  class TwoRowHeader extends HTMLElement {
    constructor() {
      super();
      // Search is the theme's native <predictive-search> component now (see the block file):
      // no query state, debounce timers, result caches or locale/currency formatting here.
      this.headerSearch = this.querySelector('.ai-two-row-header-search-wrapper');
      this.drawerSearch = this.querySelector('.ai-two-row-header-drawer-search');
      this.languageSelector = this.querySelector('.ai-two-row-header-language-selector');
      this.languageButton = this.querySelector('.ai-two-row-header-language-button');
      this.languageDropdown = this.querySelector('.ai-two-row-header-language-dropdown');
      this.languageOptions = this.querySelectorAll('.ai-two-row-header-language-option');
      this.burger = this.querySelector('.ai-two-row-header-burger');
      this.drawer = this.querySelector('.ai-two-row-header-drawer');
      this.drawerOverlay = this.querySelector('.ai-two-row-header-drawer-overlay');
      this.drawerClose = this.querySelector('.ai-two-row-header-drawer-close');
    }

    connectedCallback() {
      this.setupDropdownOverflow();
      this.applySearchGoLabel();
      this.setupStickyBeyondFooter();
      this.handOffSearchTarget('header');
      if (this.languageButton) this.languageButton.addEventListener('click', this.toggleLanguageDropdown.bind(this));
      if (this.languageOptions) this.languageOptions.forEach(o => o.addEventListener('click', this.handleLanguageChange.bind(this)));
      this.setupDrawer();
      this.setupAccordionAnimation();
      this.setupImageSkeletons();
      this.preloadMegaImages();
      this.boundDocClick = this.handleDocumentClick.bind(this);
      document.addEventListener('click', this.boundDocClick);
      this.boundEsc = (e) => { if (e.key === 'Escape') this.closeDrawer(); };
      document.addEventListener('keydown', this.boundEsc);
    }

    disconnectedCallback() {
      if (this.boundStickyScroll) {
        window.removeEventListener('scroll', this.boundStickyScroll);
        window.removeEventListener('resize', this.boundStickyScroll);
      }
      this.bodyResizeObserver?.disconnect();
      this.topbarResizeObserver?.disconnect();
      if (this.boundTopbarResize) window.removeEventListener('resize', this.boundTopbarResize);
      document.documentElement.classList.remove('df-topbar-away');
      document.removeEventListener('click', this.boundDocClick);
      if (this.boundEsc) document.removeEventListener('keydown', this.boundEsc);
      if (this.drawer && this.drawer.parentElement === document.body) this.drawer.remove();
      if (this.drawerOverlay && this.drawerOverlay.parentElement === document.body) this.drawerOverlay.remove();
      this.unlockScroll();
    }

    setupDropdownOverflow() {
      const panels = this.querySelectorAll('.ai-two-row-header-dropdown, .ai-two-row-header-mega');
      panels.forEach((panel) => {
        const item = panel.closest('.ai-two-row-header-nav-item');
        if (!item) return;
        item.addEventListener('mouseenter', () => {
          panel.style.left = '-20px';
          const rect = panel.getBoundingClientRect();
          const overflow = rect.right - (window.innerWidth - 20);
          if (overflow > 0) panel.style.left = `${-20 - overflow}px`;
        });
      });
    }

    setupDrawer() {
      if (!this.drawer || !this.drawerOverlay) return;
      // Move drawer + overlay to <body> so they overlay the whole page regardless of header stacking.
      [this.drawerOverlay, this.drawer].forEach((node) => {
        const existing = node.id ? document.getElementById(node.id) : null;
        if (existing && existing !== node) existing.remove();
        if (node.parentElement !== document.body) document.body.appendChild(node);
      });
      if (this.burger) this.burger.addEventListener('click', this.openDrawer.bind(this));
      if (this.drawerClose) this.drawerClose.addEventListener('click', this.closeDrawer.bind(this));
      this.drawerOverlay.addEventListener('click', this.closeDrawer.bind(this));
    }

    // Mega-menu image tiles load lazily and their src comes from collections dynamically, so on
    // slower connections the dropdown can open before the image is in. The CSS gives each tile a
    // shimmer skeleton by default; here we clear it (and fade the image in) once each image resolves.
    setupImageSkeletons() {
      var containers = [];
      this.querySelectorAll('.ai-two-row-header-mega-image').forEach(function (c) { containers.push(c); });
      if (this.drawer) this.drawer.querySelectorAll('.ai-two-row-header-drawer-mega-image').forEach(function (c) { containers.push(c); });
      containers.forEach(function (container) {
        var img = container.querySelector('img');
        if (!img || img.complete) { container.classList.add('ai-img-loaded'); return; }
        container.classList.add('ai-img-pending');
        var done = function () { container.classList.remove('ai-img-pending'); container.classList.add('ai-img-loaded'); };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }

    // Desktop mega-dropdown images are loading="lazy", so they only fetch on first hover — causing a
    // brief shimmer / pop-in and the "wonky, laggy" open Raoul saw on large screens. Warm them once the
    // page is idle (off the critical path) so every dropdown opens instantly from cache. Desktop tiles
    // only — the drawer tiles stay lazy (mobile, opened deliberately, no hover-latency problem).
    preloadMegaImages() {
      var self = this;
      var run = function () {
        self.querySelectorAll('.ai-two-row-header-mega-image img').forEach(function (img) {
          img.loading = 'eager';
          if (!img.complete && img.src) { var pre = new Image(); pre.src = img.src; }
        });
      };
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
      else if (document.readyState === 'complete') setTimeout(run, 300);
      else window.addEventListener('load', function () { setTimeout(run, 300); });
    }

    setupAccordionAnimation() {
      if (!this.drawer) return;
      var items = this.drawer.querySelectorAll('.ai-two-row-header-drawer-details, .ai-two-row-header-drawer-details2');
      var self = this;
      items.forEach(function (details) {
        var summary = details.querySelector(':scope > summary');
        var content = details.querySelector(':scope > summary + *');
        if (!summary || !content) return;
        summary.addEventListener('click', function (event) {
          event.preventDefault();
          self.toggleAccordion(details, summary, content);
        });
      });
    }

    // Accordion siblings = other panels at the same level as `details`: top-level items
    // (Flowers, Gifts, Our flower shops, ...) are all direct children of the drawer nav, so
    // ANY other top-level panel counts; nested items (e.g. Accessories, Gifts for) are scoped
    // to their own parent's `.ai-two-row-header-drawer-sub` so closing one branch never touches
    // an unrelated one.
    getAccordionSiblings(details) {
      var isTopLevel = details.classList.contains('ai-two-row-header-drawer-details');
      if (isTopLevel) {
        return Array.from(this.drawer.querySelectorAll('.ai-two-row-header-drawer-details')).filter(function (el) { return el !== details; });
      }
      var scope = details.closest('.ai-two-row-header-drawer-sub');
      if (!scope) return [];
      return Array.from(scope.querySelectorAll(':scope > .ai-two-row-header-drawer-details2')).filter(function (el) { return el !== details; });
    }

    // After a panel closes, also collapse any panel nested inside it — no animation needed,
    // it's already hidden inside the collapsing/collapsed parent — so it always reopens fresh
    // instead of showing a stale expanded sub-item from before.
    resetNestedAccordions(details) {
      details.querySelectorAll('details[open]').forEach(function (nested) {
        nested.removeAttribute('open');
        nested.style.height = '';
        nested.style.overflow = '';
      });
    }

    closeAccordionPanel(details) {
      var summary = details.querySelector(':scope > summary');
      var content = details.querySelector(':scope > summary + *');
      if (summary && content) this.toggleAccordion(details, summary, content);
    }

    async toggleAccordion(details, summary, content) {
      if (details._animating) return;
      var opening = !details.open;

      // Only one panel open per level at a time: opening this one closes any other open
      // sibling first, so two menus (or two sub-menus) can never be open together.
      if (opening) {
        var self = this;
        this.getAccordionSiblings(details).forEach(function (sibling) {
          if (sibling.open && !sibling._animating) self.closeAccordionPanel(sibling);
        });
      }

      if (typeof Motion === 'undefined') {
        details.open = opening;
        if (!opening) this.resetNestedAccordions(details);
        return;
      }
      details._animating = true;
      details.style.overflow = 'hidden';
      if (opening) {
        details.open = true;
        await Motion.timeline([
          [details, { height: [summary.clientHeight + 'px', details.scrollHeight + 'px'] }, { duration: 0.25, easing: 'ease' }],
          [content, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.15, at: '-0.1' }]
        ]).finished;
      } else {
        await Motion.timeline([
          [content, { opacity: 0 }, { duration: 0.15 }],
          [details, { height: [details.clientHeight + 'px', summary.clientHeight + 'px'] }, { duration: 0.25, at: '<', easing: 'ease' }]
        ]).finished;
        details.open = false;
        this.resetNestedAccordions(details);
      }
      details.style.height = 'auto';
      details.style.overflow = 'visible';
      details._animating = false;
    }

    openDrawer() {
      this.handOffSearchTarget('drawer');
      if (!this.drawer) return;
      // The theme defers offscreen sections with content-visibility:auto while <body data-page-rendering>
      // is set, clearing it itself on first interaction — but only via a requestIdleCallback that can lag
      // up to 1s behind the click. Clearing it ourselves before locking scroll guarantees those sections
      // are paintable the moment the drawer (and its dimmed backdrop over them) comes up.
      document.body.removeAttribute('data-page-rendering');
      // Parkeren opheffen voordat de drawer inschuift. Zie data-parked in closeDrawer().
      clearTimeout(this._parkTimer);
      this.drawer.removeAttribute('data-parked');
      this.drawer.classList.add('active');
      this.drawerOverlay.classList.add('active');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.burger) this.burger.setAttribute('aria-expanded', 'true');
      this.lockScroll();
    }

    // Hoe lang na het sluiten de drawer geparkeerd mag worden: precies zolang zijn eigen
    // uitschuif-animatie duurt, uitgelezen uit de CSS in plaats van hier hardcoded. Zet je de
    // snelheid in het blok anders, dan volgt deze timer vanzelf. Een hardcoded kopie zou
    // stilzwijgend uit de pas gaan lopen en de drawer halverwege laten verdwijnen.
    // De 50ms marge voorkomt dat we op het laatste frame van de animatie parkeren.
    parkDelay() {
      try {
        const cs = getComputedStyle(this.drawer);
        const props = cs.transitionProperty.split(',').map((v) => v.trim());
        const durs = cs.transitionDuration.split(',').map((v) => {
          const t = v.trim();
          return t.endsWith('ms') ? parseFloat(t) : parseFloat(t) * 1000;
        });
        const i = props.indexOf('transform');
        const ms = i !== -1 && durs[i] > 0 ? durs[i] : Math.max.apply(null, durs);
        if (ms > 0) return ms + 50;
      } catch (e) {}
      return 650;
    }

    closeDrawer() {
      this.handOffSearchTarget('header');
      if (!this.drawer || !this.drawer.classList.contains('active')) return;
      this.drawer.classList.remove('active');
      this.drawerOverlay.classList.remove('active');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
      this.unlockScroll();
      // Wegschuiven met transform alleen is niet genoeg: de drawer blijft daarna als fixed
      // laag tegen de boven- en onderrand meegetekend, en dan vult iOS Safari zijn balken
      // weer met een vlakke kleur. Zie het <style> blok in layout/theme.liquid.
      // Bewust een timer en geen transitionend of een visibility-transition: die lopen niet
      // altijd af, en dan blijft de laag staan zonder dat je het ziet.
      clearTimeout(this._parkTimer);
      this._parkTimer = setTimeout(() => {
        if (this.drawer && this.drawer.getAttribute('aria-hidden') === 'true') {
          this.drawer.setAttribute('data-parked', '');
        }
      }, this.parkDelay());
    }

    // Scroll lock = the theme's own modal mechanism (overflow:hidden + touch-action:none, CSS
    // in the block), under our own class name so we never fight the theme's modal layer-counter.
    // Applied to BOTH <html> and <body>: theme.liquid's own base stylesheet sets body{overflow-y:
    // visible}, and in standards mode <html> (not <body>) is the element that actually scrolls the
    // page — locking body alone left <html> free to scroll underneath the open drawer. The page is
    // never repositioned: the old body{position:fixed} approach collapsed the document, making iOS
    // Safari recalculate its viewport/toolbar and paint a white (canvas-colored) block behind the
    // bottom search bar while the menu was open.
    lockScroll() {
      document.documentElement.classList.add('ai-two-row-header-lock');
      document.body.classList.add('ai-two-row-header-lock');
    }
    unlockScroll() {
      document.documentElement.classList.remove('ai-two-row-header-lock');
      document.body.classList.remove('ai-two-row-header-lock');
    }

    // Keeps the header pinned over the footer. CSS `position: sticky` on the section is bounded by
    // its containing block (<body>), and this theme's footer is absolutely positioned, so body's box
    // ends ~1 footer-height above the real page bottom and sticky lets go over that last stretch.
    // Past that point we hand over to `position: fixed` via a class (see the block's CSS). The
    // section keeps its space in flow either way, so the hand-off doesn't shift the page.
    setupStickyBeyondFooter() {
      this.stickySection = this.closest('.shopify-section');
      if (!this.stickySection) return;
      if (getComputedStyle(this.stickySection).position !== 'sticky') return;

      this.setupTopbarOffset();

      const sync = this.syncStickyPin.bind(this);
      this.boundStickyScroll = theme.utils && theme.utils.throttle ? theme.utils.throttle(sync) : sync;
      window.addEventListener('scroll', this.boundStickyScroll, { passive: true });
      window.addEventListener('resize', this.boundStickyScroll);

      // Lazy-loaded sections change the page height long after load, which moves the hand-off point.
      if (window.ResizeObserver) {
        this.bodyResizeObserver = new ResizeObserver(() => this.boundStickyScroll());
        this.bodyResizeObserver.observe(document.body);
      }

      this.syncStickyPin();
    }

    // Split out of the scroll handler on purpose: theme.utils.throttle is requestAnimationFrame
    // based, so it never runs in a backgrounded tab and the pin state can't be exercised through
    // real scroll events in a headless check. Calling this directly makes the behaviour testable.
    syncStickyPin() {
      if (!this.stickySection) return;

      // body.offsetHeight, not scrollHeight: we need where the CONTAINING BLOCK ends, which is
      // exactly where sticky stops working. scrollHeight would include the overflowing footer.
      this.syncTopbarReveal();

      const limit = document.body.offsetHeight - this.stickySection.offsetHeight;
      const pinned = window.scrollY > limit;
      this.stickySection.classList.toggle('ai-two-row-header-pinned', pinned);
      // Deliberately the HEADER's threshold, not the bar's own. The bar is 79px shorter, so on its
      // own numbers it would let go of the page later and a gap would open between the two for that
      // stretch. One threshold for both keeps them glued through the hand-off.
      if (this.topbarSection) this.topbarSection.classList.toggle('ai-two-row-header-topbar-pinned', pinned);
    }

    // Parks the USP bar above this header out of sight while you scroll down and brings it back the
    // moment you scroll up, so the bar costs screen height only when you are heading somewhere. The
    // header itself never hides. Both moves are `top` transitions in the block's CSS; this only
    // decides the state.
    syncTopbarReveal() {
      if (!this.topbarSection) return;

      const y = Math.max(0, window.scrollY);
      const barHeight = this.topbarSection.offsetHeight;
      const previous = this.lastScrollY === undefined ? y : this.lastScrollY;
      const delta = y - previous;
      this.lastScrollY = y;

      // Above the bar's own height it is still sitting in flow and nothing is pinned yet, so the
      // parked state would be invisible anyway and toggling it there only causes flicker on the
      // rubber-band bounce at the top of the page.
      if (y <= barHeight) {
        this.setTopbarAway(false);
        return;
      }
      // A dead zone, not a plain sign check: momentum scrolling and iOS's overscroll both deliver
      // single stray pixels in the wrong direction, which would flip the bar back and forth.
      if (delta > 4) this.setTopbarAway(true);
      else if (delta < -4) this.setTopbarAway(false);
    }

    setTopbarAway(away) {
      if (this.topbarAway === away) return;
      this.topbarAway = away;
      document.documentElement.classList.toggle('df-topbar-away', away);
    }

    // Publishes the height of the USP bar above this header as --df-topbar-h, which the block's CSS
    // uses as the sticky offset for the header on mobile: below 990px the bar sticks at the top edge
    // and the header has to stop underneath it. Measured rather than hardcoded because the bar's
    // height is content-driven (47px today) and the theme's own --topbar-height is a rounded 48px.
    // The bar is found through its own class, the same hook the block's CSS matches on, so both stop
    // together if the bar is ever moved out of the header group.
    setupTopbarOffset() {
      const bar = document.querySelector('.custom-usp-bar');
      this.topbarSection = bar && bar.closest('.shopify-section');
      if (!this.topbarSection) return;

      const publish = () => {
        // offsetHeight, so the bar's own env(safe-area-inset-top) padding is included in the offset.
        document.documentElement.style.setProperty('--df-topbar-h', this.topbarSection.offsetHeight + 'px');
      };
      publish();

      if (window.ResizeObserver) {
        this.topbarResizeObserver = new ResizeObserver(publish);
        this.topbarResizeObserver.observe(this.topbarSection);
      } else {
        this.boundTopbarResize = publish;
        window.addEventListener('resize', publish);
      }
    }

    // Optional override for the native Go button's label. The theme's predictive-search snippet
    // prints `general.search.go` from the locale files, which a block setting can't reach — so the
    // label is swapped here instead of forking the snippet. Empty setting = keep the translation,
    // which also keeps every other language correct by default.
    applySearchGoLabel() {
      const label = (this.dataset.searchGoLabel || '').trim();
      if (!label) return;

      const target = this.querySelector('.search__go .btn-text');
      if (target) target.textContent = label;
    }

    // Both search fields (the header bar and the one in the mobile menu) are the theme's own
    // <predictive-search>, and both live in this single section. assets/search.js resolves where to
    // put results with document.getElementById('PredictiveSearchResults-' + sectionId), which only
    // ever returns the FIRST match — so with two identical ids the drawer's field would look alive
    // and render nothing. Instead of forking the snippet, that id is treated as a token exactly one
    // field holds at a time, handed to whichever field the visitor is actually using. Fetch URL,
    // response cache, debounce, Clear, Escape and the recommendations are all untouched native
    // behaviour, so the two fields behave identically.
    handOffSearchTarget(scope) {
      const owner = scope === 'drawer' ? this.drawerSearch : this.headerSearch;
      const other = scope === 'drawer' ? this.headerSearch : this.drawerSearch;
      if (!owner || !other) return;

      const sectionId = owner.querySelector('form.search__form')?.dataset.sectionId;
      if (!sectionId) return;
      const id = 'PredictiveSearchResults-' + sectionId;

      // park the current holder's id so it can be handed back later
      const holder = other.querySelector('[id="' + id + '"]');
      if (holder) {
        holder.removeAttribute('id');
        holder.setAttribute('data-ai-parked-id', id);
      }

      const target = owner.querySelector('[data-ai-parked-id="' + id + '"]');
      if (target) {
        target.id = id;
        target.removeAttribute('data-ai-parked-id');
      }

      // The theme opens dropdowns with ONE global body class (predictive-search-open) and both of
      // this block's fields are `.search-inline`, so that class alone popped open the header field's
      // panel too while you were typing in the drawer. This marks the field actually in use; the
      // block's CSS keeps the other one's panels closed. The two bars stay independent.
      owner.classList.add('ai-search-active');
      other.classList.remove('ai-search-active');
    }

    // Only the language dropdown is ours to dismiss now — the search dropdown closes itself
    // via the theme's own overlay click and Escape handling in assets/search.js.
    handleDocumentClick(event) {
      if (this.languageSelector && !this.languageSelector.contains(event.target)) this.hideLanguageDropdown();
    }

    toggleLanguageDropdown() {
      if (this.languageDropdown.classList.contains('active')) this.hideLanguageDropdown();
      else this.showLanguageDropdown();
    }

    showLanguageDropdown() { this.languageDropdown.classList.add('active'); this.languageButton.setAttribute('aria-expanded', 'true'); }
    hideLanguageDropdown() { this.languageDropdown.classList.remove('active'); this.languageButton.setAttribute('aria-expanded', 'false'); }

    // Plain path-based redirect instead of Shopify's native /localization form: this store's
    // "nl" locale has no Market web presence attached to it, so the native form's PUT is a silent
    // no-op (it resolves "nl" as a country/market hint and redirects back unchanged) even though
    // /nl/ itself is a perfectly valid, directly-reachable published-locale subfolder. Going
    // straight to the URL sidesteps that broken switch entirely.
    //
    // The roots come from Liquid's own `shop_locale.root_url` (data-root-url per option,
    // data-locale-root for the page's current locale) rather than a hardcoded "/nl". Shopify fills
    // those in per MARKET, so on a market that serves its own subfolder — Belgium is on
    // /en-be/ — swapping a literal prefix would have produced /nl/en-be/... and 404'd. Rebuilding
    // the path off the two roots is the only version that survives a market with a web presence.
    //
    // Each option also carries a real href to its locale root, so without JS the link still goes
    // somewhere sensible instead of nowhere, and middle-click and open-in-new-tab behave.
    handleLanguageChange(event) {
      const nextRoot = event.currentTarget.dataset.rootUrl;
      if (!nextRoot) return; // No root from Liquid: leave the anchor's own href alone.
      event.preventDefault();

      // Both roots are normalised to "" for the primary locale and "/xx" otherwise, so joining is
      // the same operation in every combination and can never emit a doubled slash.
      const trim = (value) => (value || '/').replace(/\/+$/, '');
      const currentRoot = trim(this.dataset.localeRoot);
      const targetRoot = trim(nextRoot);

      let path = window.location.pathname;
      if (currentRoot && (path === currentRoot || path.startsWith(currentRoot + '/'))) {
        path = path.slice(currentRoot.length);
      }

      window.location.href = (targetRoot + path || '/') + window.location.search;
    }

  }
  customElements.define('two-row-header', TwoRowHeader);
})();
